import "@/App.css";
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  ShieldCheck,
  Volume2,
  UtensilsCrossed,
  Sparkles,
  Heart,
  Users,
  MapPin,
  Star,
  BadgeCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

// Icons used for the 6 "values" cards (kept fixed; admin edits text only)
const VALUE_ICONS = [
  <ShieldCheck size={22} className="text-[#f4d089]" />,
  <Volume2 size={22} className="text-[#f4d089]" />,
  <UtensilsCrossed size={22} className="text-[#f4d089]" />,
  <Sparkles size={22} className="text-[#f4d089]" />,
  <BadgeCheck size={22} className="text-[#f4d089]" />,
  <Heart size={22} className="text-[#f4d089]" />,
];

// Icons used for the 3 "quick info" cards
const QUICK_ICONS = [
  <Users size={24} className="text-[#f4d089] mx-auto" />,
  <MapPin size={24} className="text-[#f4d089] mx-auto" />,
  <Star size={24} className="text-[#f4d089] mx-auto" />,
];

const About = () => {
  const [c, setC] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/content/about`)
      .then((r) => {
        if (!cancelled) setC(r.data);
      })
      .catch(() => {
        if (!cancelled) setC({}); // render with empty fallbacks rather than block
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!c) {
    return (
      <div
        className="min-h-screen bg-[#0a0604] text-[#bfb393] flex items-center justify-center"
        data-testid="about-loading"
      >
        Loading…
      </div>
    );
  }

  const stats = c.stats || [];
  const values = c.values || [];
  const milestones = c.milestones || [];
  const quickInfo = c.quick_info || [];

  return (
    <div className="min-h-screen bg-[#0a0604] text-[#e8dcc0]" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <a href="/" className="flex items-center" data-testid="cinevibe-logo">
  <div className="w-16 h-16 rounded-full overflow-hidden border border-[#d4a574]/40 flex items-center justify-center bg-[#0a0604]">
    <img src="/images/logo.png" alt="CineVibe logo" className="w-full h-full object-cover" />
  </div>
</a>

          <GoldButton
            data-testid="about-header-book"
            onClick={() => (window.location.href = "/book")}
            className="text-sm px-5 py-2.5"
          >
            BOOK NOW
          </GoldButton>
        </Section>
      </header>

      {/* Hero */}
      <Section className="pt-12 pb-10 text-center">
        <span
          className="inline-block bg-[#f4d089] text-[#1a0f06] text-[11px] font-bold tracking-wider px-3 py-1 rounded"
          data-testid="about-eyebrow"
        >
          {c.eyebrow}
        </span>
        <h1
          className="font-display text-white text-3xl sm:text-5xl mt-4 leading-tight"
          data-testid="about-title"
        >
          {c.title_pre}
          <span className="text-[#f4d089]">{c.title_highlight}</span>
          {c.title_post}
        </h1>
        <p
          className="text-[#bfb393] mt-4 max-w-2xl mx-auto text-[15px] sm:text-[16px] leading-relaxed"
          data-testid="about-subtitle"
        >
          {c.subtitle}
        </p>
      </Section>

      {/* Hero image strip */}
      <Section className="pb-14">
        <div className="rounded-2xl overflow-hidden border border-[#3a2a14]/70 relative h-[260px] sm:h-[420px]">
          <img
            src="/images/hero.jpg"
            alt="CineVibe private screen"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0604] via-[#0a0604]/40 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] tracking-[0.18em] text-[#f4d089]" data-testid="about-hero-eyebrow">
                {c.hero_eyebrow}
              </div>
              <h3
                className="font-display text-white text-2xl sm:text-3xl mt-1"
                data-testid="about-hero-title"
              >
                {c.hero_title}
              </h3>
            </div>
            <div
              className="flex items-center gap-2 text-[#7ad99c] text-sm"
              data-testid="about-hero-rating"
            >
              <Star size={16} className="text-[#f4d089] fill-[#f4d089]" /> {c.hero_rating}
            </div>
          </div>
        </div>
      </Section>

      {/* Story */}
      <Section className="pb-16 grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="text-[11px] tracking-[0.18em] text-[#f4d089]">{c.story_eyebrow}</div>
          <h2 className="font-display text-white text-2xl sm:text-4xl mt-2" data-testid="about-story-heading">
            {c.story_heading}
          </h2>
        </div>
        <div className="space-y-4 text-[#cbbf9f] text-[15px] leading-relaxed">
          <p data-testid="about-story-p1">{c.story_p1}</p>
          <p data-testid="about-story-p2">{c.story_p2}</p>
          <p data-testid="about-story-p3">
            {c.story_p3_pre}
            <span className="text-[#f4d089]">{c.story_p3_highlight}</span>
            {c.story_p3_post}
          </p>
        </div>
      </Section>

      {/* Stats */}
      <div className="border-y border-[#3a2a14]/60 bg-gradient-to-b from-[#0d0905] to-[#14100a]">
        <Section className="py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`about-stat-${i}`}>
              <div className="font-display text-[#f4d089] text-3xl sm:text-4xl">{s.v}</div>
              <div className="text-[11px] sm:text-xs tracking-[0.18em] text-[#bfb393] mt-1 uppercase">
                {s.l}
              </div>
            </div>
          ))}
        </Section>
      </div>

      {/* What we stand for */}
      <Section className="py-16">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.18em] text-[#f4d089]">{c.values_eyebrow}</div>
          <h2 className="font-display text-white text-2xl sm:text-4xl mt-2" data-testid="about-values-heading">
            {c.values_heading}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {values.map((v, i) => (
            <Card key={i} className="p-6" data-testid={`about-value-${i}`}>
              <div className="w-11 h-11 rounded-full bg-[#1a0f06] border border-[#d4a574]/30 flex items-center justify-center mb-4">
                {VALUE_ICONS[i] || VALUE_ICONS[0]}
              </div>
              <h3 className="text-white font-semibold text-lg">{v.title}</h3>
              <p className="text-[#bfb393] text-sm mt-2 leading-relaxed">{v.desc}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Timeline */}
      <div className="border-t border-[#3a2a14]/60">
        <Section className="py-16">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.18em] text-[#f4d089]">{c.timeline_eyebrow}</div>
            <h2
              className="font-display text-white text-2xl sm:text-4xl mt-2"
              data-testid="about-timeline-heading"
            >
              {c.timeline_heading}
            </h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[#3a2a14]" />
            <div className="space-y-8 md:space-y-12">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`md:grid md:grid-cols-2 md:gap-10 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
                  data-testid={`about-milestone-${i}`}
                >
                  <div className={`${i % 2 === 0 ? "md:text-right md:pr-10" : "md:pl-10"}`}>
                    <div className="font-display text-[#f4d089] text-3xl">{m.year}</div>
                    <h3 className="text-white text-lg font-semibold mt-1">{m.title}</h3>
                  </div>
                  <div className={`${i % 2 === 0 ? "md:pl-10" : "md:text-right md:pr-10"} mt-2 md:mt-0`}>
                    <p className="text-[#bfb393] text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* Quick info row */}
      <Section className="pb-16">
        <Card className="p-6 sm:p-10 grid sm:grid-cols-3 gap-6 text-center">
          {quickInfo.map((q, i) => (
            <div key={i} data-testid={`about-quick-${i}`}>
              {QUICK_ICONS[i] || QUICK_ICONS[0]}
              <div className="text-white font-semibold mt-3">{q.title}</div>
              <p className="text-[#bfb393] text-sm mt-1">{q.desc}</p>
            </div>
          ))}
        </Card>
      </Section>

      {/* CTA */}
      <div className="border-t border-[#3a2a14]/60 bg-gradient-to-b from-[#0d0905] to-[#14100a]">
        <Section className="py-14 text-center">
          <h3 className="font-display text-2xl sm:text-3xl text-white" data-testid="about-cta-heading">
            {c.cta_heading}
          </h3>
          <p className="text-[#bfb393] mt-3 max-w-xl mx-auto" data-testid="about-cta-subtitle">
            {c.cta_subtitle}
          </p>
          <div className="flex justify-center mt-6 gap-3 flex-wrap">
            <GoldButton
              onClick={() => (window.location.href = "/book")}
              data-testid="about-cta-book"
              className="text-base tracking-[0.15em] flex items-center gap-2"
            >
              {c.cta_book_btn} <ArrowRight size={18} />
            </GoldButton>
            <button
              onClick={() => (window.location.href = "/franchise")}
              data-testid="about-cta-franchise"
              className="text-sm tracking-[0.15em] border border-[#d4a574]/40 rounded-md px-6 py-3 text-[#f4d089] hover:bg-[#1a0f06] transition"
            >
              {c.cta_franchise_btn}
            </button>
          </div>
        </Section>
      </div>

      <footer
        className="border-t border-[#3a2a14]/60 py-6 text-center text-xs text-[#a89878] leading-relaxed"
        data-testid="about-footer"
      >
        © {new Date().getFullYear()} {c.footer_text}<br />
        BnBBuddy Property Management Services Pvt Ltd · GSTIN: 07AALCB5876H1Z8
      </footer>
    </div>
  );
};

export default About;
